import InstallAppModal from '@components/shared/Layout/InstallAppModal'
import NativeOfflineBootstrap from '@components/NativeOfflineBootstrap'
import Footer from '@components/shared/Layout/Footer'
import Nav from '@components/shared/Layout/Nav/Nav'
import { AuthContextProvider } from '@contexts/AuthContext'
import { UserDataProvider } from '@contexts/UserDataContext'
import { FontSizesProvider } from '@hooks/useFontSizes'
import { ErrorBoundary, Provider as RollbarProvider } from '@rollbar/react'
import { DefaultSeo } from 'next-seo'
import { ThemeProvider } from 'next-themes'
import type { AppProps } from 'next/app'
import Script from 'next/script'
import NextNProgress from 'nextjs-progressbar'
import { useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import SEO from '../next-seo.config'
import '../styles/globals.css'
import { clientConfig } from '../utils/rollbar'
import ServiceWorkerRegister from '@components/ServiceWorkerRegister'

const umiEvent = (e: MouseEvent) => {
  const element = e.target as HTMLElement

  const classList = element.closest('[class*="umi-event--"]')?.classList
  const umamiClass = classList?.value.split(' ').find((className) => {
    return className.includes('umi-event--')
  })

  if (umamiClass) {
    const umamiEvent = umamiClass.split('--')[1]

    if (umamiEvent) {
      window.umami?.track(umamiEvent)
    }
  }
}

function App({ Component, pageProps }: AppProps) {
  useEffect(() => {
    const clarityScript = `(function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
  })(window, document, "clarity", "script", "hjwafam3rd");`
    const clarity = document.createElement('script')
    clarity.async = true
    clarity.defer = true
    clarity.innerHTML = clarityScript

    document.body.appendChild(clarity)

    document.addEventListener('click', umiEvent)

    return () => {
      document.removeEventListener('click', umiEvent)
    }
  }, [])

  return (
    <>
      {process.env.NODE_ENV === 'development' ? null : (
        <Script
          async
          defer
          data-performance="true"
          data-website-id="65fc8ba4-b28c-40be-9bbc-0126f601d667"
          src="https://umi.kurantime.com/script.js"
        ></Script>
      )}
      <NextNProgress color="var(--color-primary)" height={4} />
      <AuthContextProvider>
        <UserDataProvider>
          <FontSizesProvider>
            <ThemeProvider defaultTheme="light" attribute="class">
              <RollbarProvider config={clientConfig}>
                <DefaultSeo {...SEO} />
                <NativeOfflineBootstrap />
                <InstallAppModal />
                <Nav />
                <div
                  className="m-auto min-h-[calc(100vh-12rem)] pt-1 px-3 sm:px-5"
                  style={{ maxWidth: '1000px' }}
                >
                  <ErrorBoundary>
                    <Component {...pageProps} />
                  </ErrorBoundary>
                </div>
                <Footer />
                <Toaster position="bottom-center" />
              </RollbarProvider>
            </ThemeProvider>
          </FontSizesProvider>
        </UserDataProvider>
      </AuthContextProvider>
      <ServiceWorkerRegister />
    </>
  )
}

export default App
